import { frameId, type ManifestAppearanceState, type ManifestObjectAppearance, type TileRef } from "daedalus-core";
import { objectTypeOfKind } from "../protocol/kinds.ts";
import { Component, type EntityState, type SnapshotRecord } from "../protocol/snapshot.ts";

/**
 * Transition playback on the client (ADR-058 decision 5 and its amendment). A thing's state rides the
 * snapshot's animation bits; the client watches that index change and, where the appearance table carries
 * frames for the change, plays them once and then holds the new state's frame. The rules this module
 * enforces:
 *
 * - First sight is not a change. A handle the client does not hold, a spawn record, or a held handle whose
 *   appearance differs takes its state's frame directly, so joining, a thing scrolling into interest and a
 *   version swap (which calls `reset`) animate nothing.
 * - The latest state wins. A change that arrives mid-animation abandons the one playing and starts its own
 *   transition, or cuts when it has none; nothing queues.
 * - It is decoration. What a thing blocks and which verbs it offers come from `thingLook` over the state
 *   byte the view holds, from the instant it changes; the frames never feed them.
 * - An index the appearance cannot resolve draws the initial state, animates nothing, and is reported once
 *   per appearance.
 * - A change is told as it is seen. A listener hears each change between two states the appearance resolves,
 *   with or without frames to play, and never a first sight; a thing's sound rides it (ADR-085's amendment
 *   for P1.9-T79).
 *
 * Every frame id is computed once per appearance at construction and a held thing's record is pooled, so
 * asking what to draw allocates nothing.
 */

/**
 * A frame to draw: the tile reference and its atlas frame id, both computed once.
 */
export interface ThingFrame {
  readonly ref: TileRef;
  readonly id: string;
}

/**
 * One transition's frames and the time each ends, in milliseconds from its start.
 */
interface PreparedTransition {
  readonly frames: readonly ThingFrame[];
  readonly ends: readonly number[];
}

/**
 * One appearance's frames ready to draw: a frame per state, and the transitions keyed by
 * `from * STATE_KEY + to`.
 */
interface PreparedAppearance {
  readonly look: ManifestObjectAppearance;
  readonly states: readonly ThingFrame[];
  readonly transitions: ReadonlyMap<number, PreparedTransition>;
}

/**
 * What the client holds for one thing: its appearance, the state index it last saw, whether that index
 * resolved, and the transition playing with its start time.
 */
interface Held {
  appearance: number;
  anim: number;
  resolved: boolean;
  playing: PreparedTransition | null;
  startMs: number;
}

/**
 * Multiplier that keys a transition by its pair of state indices; above the 64 states the animation bits
 * carry (ADR-042).
 */
const STATE_KEY = 64;

/**
 * The state an appearance shows for a state index: the state at that index, or the initial state when the
 * index names none, so a client running against a table that lost a state draws something rather than
 * throwing (ADR-058 amendment 4).
 *
 * @param look The appearance.
 * @param anim The state index from the snapshot.
 * @returns The state.
 */
export function appearanceState(look: ManifestObjectAppearance, anim: number): ManifestAppearanceState {
  return look.states[anim] ?? (look.states[look.initial] as ManifestAppearanceState);
}

/**
 * What a thing in the view is (ADR-053): its appearance and the state its record's animation bits name,
 * with the frame it rests on, the footprint to stamp and the cells it is usable from, all following the
 * state byte and never a transition's frames (ADR-058 amendment 1). An index past the states resolves to
 * the initial state rather than drawing nothing (ADR-058 amendment 4).
 *
 * @param appearances The world version's appearance table.
 * @param state The entity's record as the view holds it.
 * @returns The appearance and its current state, or null for a character or an appearance the table lacks.
 */
export function thingLook(appearances: readonly ManifestObjectAppearance[], state: { readonly kind: number; readonly appearance: number; readonly anim: number }): { readonly look: ManifestObjectAppearance; readonly state: ManifestAppearanceState } | null {
  if (objectTypeOfKind(state.kind) === undefined) {
    return null;
  }
  const look = appearances[state.appearance];
  return look === undefined ? null : { look, state: appearanceState(look, state.anim) };
}

/**
 * A tile reference with its frame id.
 *
 * @param ref The tile reference.
 * @returns The frame.
 */
function thingFrame(ref: TileRef): ThingFrame {
  return { ref, id: frameId(ref.tileset, null, ref.tile) };
}

/**
 * Precomputes an appearance's frames.
 *
 * @param look The appearance.
 * @returns The prepared appearance.
 */
function prepare(look: ManifestObjectAppearance): PreparedAppearance {
  const transitions = new Map<number, PreparedTransition>();
  for (const transition of look.transitions ?? []) {
    const ends: number[] = [];
    let total = 0;
    for (const duration of transition.durations) {
      total += duration;
      ends.push(total);
    }
    transitions.set(transition.from * STATE_KEY + transition.to, { frames: transition.frames.map(thingFrame), ends });
  }
  return { look, states: look.states.map((s) => thingFrame(s.frame)), transitions };
}

/**
 * A view holding no handle, which `reset` keeps.
 */
const NOTHING: { has(handle: number): boolean } = { has: () => false };

/**
 * The playback of every thing in a client's view.
 */
export class ThingPlayback {
  private readonly prepared: readonly PreparedAppearance[];
  private readonly held = new Map<number, Held>();
  private readonly pool: Held[] = [];
  private readonly reported = new Set<number>();

  /**
   * How many transitions have started playing, for the page's diagnostics.
   */
  public started = 0;

  /**
   * How many transitions have played to their end, counted as `frame` first shows the state's frame after one. A
   * strip a later change cut short is not counted. The page watches it to offer a thing's verb again
   * (ADR-058's amendment of 2026-10-07, item 5).
   */
  public ended = 0;

  /**
   * @param appearances The world version's appearance table.
   * @param onUnresolved Called once per appearance index with the first state index, or the first
   *   appearance index, the table cannot resolve.
   * @param onChanged Called when a held thing's state changes between two states its appearance resolves, with
   *   the thing's handle, its appearance index, the state index it left and the one it took.
   */
  public constructor(
    appearances: readonly ManifestObjectAppearance[],
    private readonly onUnresolved: (appearance: number, anim: number) => void = () => undefined,
    private readonly onChanged: (handle: number, appearance: number, from: number, to: number) => void = () => undefined,
  ) {
    this.prepared = appearances.map(prepare);
  }

  /**
   * Takes what a snapshot says a thing is. A change from the state held for the same appearance plays that
   * transition from now, replacing any still playing, and is told to the listener when both states resolve;
   * anything else is first sight and shows its state.
   *
   * @param handle The thing's handle.
   * @param appearance Its appearance index.
   * @param anim Its state index.
   * @param spawned Whether the record is a spawn in a delta: the map server believes the client never saw the
   *   thing, so it is first sight even when the handle is still held.
   * @param nowMs The client clock in milliseconds.
   */
  public observe(handle: number, appearance: number, anim: number, spawned: boolean, nowMs: number): void {
    const prepared = this.prepared[appearance];
    if (prepared === undefined) {
      this.report(appearance, anim);
    }
    const resolved = prepared !== undefined && prepared.look.states[anim] !== undefined;
    if (prepared !== undefined && !resolved) {
      this.report(appearance, anim);
    }
    const held = this.held.get(handle);
    if (held === undefined || spawned || held.appearance !== appearance) {
      const entry = held ?? this.pool.pop() ?? { appearance, anim, resolved, playing: null, startMs: 0 };
      entry.appearance = appearance;
      entry.anim = anim;
      entry.resolved = resolved;
      entry.playing = null;
      entry.startMs = nowMs;
      this.held.set(handle, entry);
      return;
    }
    if (held.anim === anim) {
      return;
    }
    const from = held.anim;
    const told = resolved && held.resolved;
    held.playing = told && prepared !== undefined ? (prepared.transitions.get(from * STATE_KEY + anim) ?? null) : null;
    this.started += held.playing === null ? 0 : 1;
    held.anim = anim;
    held.resolved = resolved;
    held.startMs = nowMs;
    if (told) {
      this.onChanged(handle, appearance, from, anim);
    }
  }

  /**
   * Drops a thing that left the view.
   *
   * @param handle The thing's handle.
   */
  public forget(handle: number): void {
    const held = this.held.get(handle);
    if (held !== undefined) {
      held.playing = null;
      this.held.delete(handle);
      this.pool.push(held);
    }
  }

  /**
   * Keeps only the things a view still holds, as after a keyframe that lists the whole view.
   *
   * @param view The handles still in view, such as the view's entity map.
   */
  public retain(view: { has(handle: number): boolean }): void {
    for (const handle of this.held.keys()) {
      if (!view.has(handle)) {
        this.forget(handle);
      }
    }
  }

  /**
   * Drops every held state, so the next record of every thing is first sight: a version swap re-seeds, it
   * does not animate (ADR-058 amendment 3).
   */
  public reset(): void {
    this.retain(NOTHING);
  }

  /**
   * The frame a thing shows now: its transition's frame while one plays, its state's frame otherwise, the
   * initial state's for an index the appearance cannot resolve.
   *
   * @param handle The thing's handle.
   * @param nowMs The client clock in milliseconds.
   * @returns The frame, or null when the thing is not held or its appearance is not in the table.
   */
  public frame(handle: number, nowMs: number): ThingFrame | null {
    const held = this.held.get(handle);
    const prepared = held === undefined ? undefined : this.prepared[held.appearance];
    if (held === undefined || prepared === undefined) {
      return null;
    }
    const playing = held.playing;
    if (playing !== null) {
      const elapsed = nowMs - held.startMs;
      for (let i = 0; i < playing.ends.length; i += 1) {
        if (elapsed < (playing.ends[i] as number)) {
          return playing.frames[i] as ThingFrame;
        }
      }
      held.playing = null;
      this.ended += 1;
    }
    return (held.resolved ? prepared.states[held.anim] : prepared.states[prepared.look.initial]) as ThingFrame;
  }

  /**
   * Whether a thing's transition strip is still playing: from the change the playback saw until the strip's last
   * frame has had its time. First sight, a cut and a thing not held are never playing. It reads only, so asking
   * neither ends a strip nor changes the frame drawn, and allocates nothing.
   *
   * @param handle The thing's handle.
   * @param nowMs The client clock in milliseconds.
   * @returns True while the strip plays.
   */
  public playing(handle: number, nowMs: number): boolean {
    const held = this.held.get(handle);
    const strip = held?.playing;
    if (held === undefined || strip === null || strip === undefined || strip.ends.length === 0) {
      return false;
    }
    return nowMs - held.startMs < (strip.ends[strip.ends.length - 1] as number);
  }

  /**
   * Reports an index the table cannot resolve, once per appearance index.
   *
   * @param appearance The appearance index.
   * @param anim The state index.
   */
  private report(appearance: number, anim: number): void {
    if (!this.reported.has(appearance)) {
      this.reported.add(appearance);
      this.onUnresolved(appearance, anim);
    }
  }
}

/**
 * Hands the things an applied snapshot carries to the playback (ADR-058 decision 5). Each thing's
 * appearance and state are read from the view after the snapshot, since a delta record carries only what
 * changed. A spawn in a delta is first sight: the map server believes the client never saw the thing. A keyframe's
 * records all carry the spawn flag, so there it counts as first sight only for a thing the playback does not
 * already hold, and a gate seen closed that a keyframe shows open still plays its opening. A removal forgets
 * the thing, and after a keyframe, which lists the whole view, everything it no longer lists is forgotten.
 * Characters are not things and are never held. Nothing is allocated.
 *
 * @param playback The playback.
 * @param record The snapshot just applied.
 * @param view The client's view after it.
 * @param nowMs The client clock in milliseconds.
 */
export function observeSnapshot(playback: ThingPlayback, record: SnapshotRecord, view: ReadonlyMap<number, EntityState>, nowMs: number): void {
  const entries = record.entities;
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i] as SnapshotRecord["entities"][number];
    const state = view.get(entry.handle);
    if (state !== undefined && objectTypeOfKind(state.kind) !== undefined) {
      playback.observe(entry.handle, state.appearance, state.anim, !record.keyframe && (entry.flags & Component.Spawn) !== 0, nowMs);
    }
  }
  const removed = record.removed;
  for (let i = 0; i < removed.length; i += 1) {
    playback.forget(removed[i] as number);
  }
  if (record.keyframe) {
    playback.retain(view);
  }
}
